import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { Field, Input, Label, Switch } from "@headlessui/react";
import { InformationCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { SimpleOption } from "../../components/input/SimpleListbox";
import Select from "../../components/input/Select";
import {
	GradeModel,
	ScoringLevelModel,
	useGetGradesQuery,
	useGetScoringLevelsQuery,
} from "../lookup/lookupApiSlice";
import { lookupGrade, lookupLevel } from "../lookup/lookupUtil";
import { ProctorCourseEditModel, useGetEditCourseInfoQuery } from "./manageCourseApiSlice";

interface Props {
	updateCourseModel: (model: ProctorCourseEditModel) => void;
	disableConfirm: (disable: boolean) => void;
}

export default function EditCourseByTeacherForm(props: Props) {
	const { course } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [model, setModel] = useState<ProctorCourseEditModel>();
	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);
	const [courseName, setCourseName] = useState("");
	const [enabled, setEnabled] = useState(false);
	const [grade, setGrade] = useState<GradeModel>();
	const [scoringLevel, setScoringLevel] = useState<ScoringLevelModel>();

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const scoringLevelResult = useGetScoringLevelsQuery();
	const scoringLevels = scoringLevelResult.data;

	const editCourseInfoResult = useGetEditCourseInfoQuery();
	const editCourseInfo = editCourseInfoResult.data;

	useEffect(() => {
		if (!course || !grades || !scoringLevels) return;
		setCourseName(course.name);
		setEnabled(course.showUserAvatars ?? false);
		const grade = lookupGrade(course.gradeId, grades);
		setGrade(grade);
		setScoringLevel(lookupLevel(scoringLevels, grade));
	}, [course, grades, scoringLevels]);

	useEffect(() => {
		if (model) props.updateCourseModel(model);
	}, [model]);

	useEffect(() => {
		if (!courseName) {
			props.disableConfirm(true);
		} else {
			props.disableConfirm(false);
		}

		if (model) {
			const newModel = {
				...model,
				name: courseName,
				disableUserImages: !enabled,
				gradeId: grade!.gradeId,
			};
			setModel(newModel);
			if (scoringLevels) setScoringLevel(lookupLevel(scoringLevels, grade));
		}
	}, [grade, courseName, enabled]);

	useEffect(() => {
		if (!editCourseInfo) return;
		const { name, gradeId, disableUserImages, ...rest } = editCourseInfo;
		setModel((prevModel) => {
			if (!prevModel) return editCourseInfo;
			return {
				...rest,
				name: prevModel.name,
				gradeId: prevModel.gradeId,
				disableUserImages: prevModel.disableUserImages,
			};
		});
	}, [editCourseInfo]);

	useEffect(() => {
		const options: SimpleOption[] =
			grades?.map((grade) => {
				return {
					value: grade.gradeId,
					label: grade.name,
				} as SimpleOption;
			}) ?? [];

		setGradeOptions(options);
	}, [grades]);

	const handleGradeChange = (gradeId: number) => {
		if (!grades) return;
		const grade = lookupGrade(gradeId, grades);
		setGrade(grade);
	};

	return (
		<div>
			<Field className="mt-2">
				<Label htmlFor="courseName" className="font-semibold text-sm">
					Course Name
				</Label>
				<Input
					id="courseNameTextBox"
					name="courseName"
					type="text"
					value={courseName}
					className="block w-full mt-1 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
					onChange={(e) => setCourseName(e.target.value)}
				/>
			</Field>

			<div className="flex justify-between gap-4 mt-2">
				<Field className="w-1/2">
					<Label className="font-semibold text-sm">Grade</Label>
					{editCourseInfo?.canProctorEditCourseGrade ? (
						<Select
							value={grade?.gradeId}
							setValue={(v) => handleGradeChange(v as number)}
							simpleListBoxId="gradeFilter"
							options={gradeOptions}
							fieldClasses="mt-1"
							simpleListBoxClasses="w-full"
						/>
					) : (
						<Input
							type="text"
							id="grade"
							disabled={true}
							readOnly={true}
							className="w-full mt-1 rounded-lg border-none bg-gray-200 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={grade?.name}
						/>
					)}
				</Field>
				<Field className="w-1/2">
					<div className="flex">
						<Label
							htmlFor="course-grade"
							className="block font-semibold text-sm text-gray-900">
							Scoring Level
						</Label>
						<ScoringLevelInfo className="ml-2" />
					</div>
					<Input
						type="text"
						id="scoring-level"
						disabled={true}
						readOnly={true}
						className="w-full mt-2 rounded-lg border-none bg-gray-200 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						value={scoringLevel?.name ?? "unknown"}
					/>
				</Field>
			</div>

			{config?.enableAvatarImages && (
				<Field className="pt-2">
					<Label htmlFor="userAvatar" className="font-semibold text-sm">
						User Avatar
					</Label>
					<div className="mt-1">
						{editCourseInfo?.disableUserImagesByDistrict ||
						editCourseInfo?.disableUserImagesBySchool ? (
							<div className="flex items-center ml-1">
								<Switch
									checked={false}
									disabled={true}
									name="userAvatar"
									className="group relative inline-flex items-center h-7 w-12 shrink-0 rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden">
									<span
										aria-hidden="true"
										className="pointer-events-none absolute inset-0 flex items-center justify-center size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
										<LockClosedIcon className="size-5 items-center text-gray-500" />
									</span>
								</Switch>

								<p className="ml-2 text-sm">
									{editCourseInfo?.disableUserImagesByDistrict
										? "User avatars are disabled by your district."
										: "User avatars are disabled by your school."}
								</p>
							</div>
						) : (
							<div className="flex items-center ml-2">
								<Label className="sr-only">User Avatar Toggle</Label>
								<Switch
									checked={enabled}
									name="userAvatar"
									onChange={setEnabled}
									className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
									<span
										aria-hidden="true"
										className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
									/>
								</Switch>

								<p className="ml-2 text-sm">
									{enabled
										? "User avatars are enabled for your students to use."
										: "User avatars are disabled for students to use."}
								</p>
							</div>
						)}
					</div>
				</Field>
			)}
		</div>
	);
}

function ScoringLevelInfo(props: { className?: string }) {
	const [isInfoVisible, setIsInfoVisible] = useState(false);

	const handleClick = () => {
		setIsInfoVisible(!isInfoVisible);
	};
	return (
		<>
			<InformationCircleIcon
				data-tooltip-id="scoring-level-info"
				className={`${props.className} h-5 text-gray-700 cursor-pointer`}
				onClick={handleClick}
			/>
			<Tooltip
				id="scoring-level-info"
				place="top"
				className="w-40"
				isOpen={isInfoVisible}
				openOnClick={true}>
				<div className="w-80">
					<p>
						Course scoring level is banded, based on the course grade. When a student is
						added to a course, they will be assigned the current course scoring level,
						but a teacher may adjust it using the <strong>Edit Student</strong> action.
					</p>
					<p className="mt-2">
						If a course grade change causes its scoring level to change, the scoring
						level for students already in the course is not adjusted.
					</p>
				</div>
			</Tooltip>
		</>
	);
}
